/**
 * TODO: check possibility to unify this component with OfferListCard
 * present in Timeline
 */
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { twJoin, twMerge } from 'tailwind-merge';

import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import Image from 'components/Image';
import Tags from 'components/Timeline/OfferCard/V1/Tags';

import useExclusiveOffer from 'hooks/useExclusiveOffer';

import { sendEvent } from 'lib/gtag';
import { getOfferImageLink, toOfferImageAltText } from 'lib/image';
import { toOfferLink } from 'lib/links';
import { isValidApprovedOffer } from 'lib/offer';

import { useUser } from 'providers/UserProvider';

import Badge from './Badge';
import GoToStoreButton from './GotoStoreButton';
import OfferStats from './OfferStats';

import OFFER from 'constants/offer';

const OfferStatusBadge = dynamic(() => import('./OfferStatusBadge'));

const Price = dynamic(() => import('./Price'));

const PublishDetails = dynamic(() => import('components/PublishDetails'));

const OfferSearchListCard = ({
  className,
  disabled = false,
  gaEventCategory,
  offerClicks = null,
  offerComments,
  offerCoupon,
  offerId,
  offerImagePriority = false,
  offerLikes,
  offerOldPrice = null,
  offerPhoto,
  offerPrice = null,
  offerPriceType = null,
  offerPublished = null,
  offerSlug,
  offerStatusName,
  offerTags = [],
  offerTitle,
  offerUserVisibility,
  selectedOfferStats,
  showGoToStoreButton = false,
  showOfferPrice = false,
  showOfferStatusBadge = false,
  showPublishDetails,
  storeDomain = null,
  storeImage = null,
  userName = null,
}) => {
  const {
    isOfferBlocked,
    formatGaEventActionByVisibility,
    onUnloggedUserTriesOpenExclusiveOffer,
  } = useExclusiveOffer();
  const { isLoggedIn } = useUser();

  const onOfferClick = () => {
    sendEvent({ action: 'offer_click', category: gaEventCategory });
  };

  const onClick = (event) => {
    sendEvent({
      action: formatGaEventActionByVisibility(
        'click__offer',
        offerUserVisibility
      ),
      category: gaEventCategory,
      logged: isLoggedIn,
    });

    if (!isOfferBlocked(offerUserVisibility, offerSlug)) {
      return;
    }

    event.preventDefault();
    onUnloggedUserTriesOpenExclusiveOffer(offerSlug, offerUserVisibility);
    onOfferClick();
  };

  return (
    <NextLink href={toOfferLink(offerSlug)} prefetch={false} passHref>
      <Anchor disabled={disabled} onClick={onClick}>
        <div
          className={twMerge(
            'relative flex flex-wrap border-b border-solid border-neutral-high-300 px-4 pb-5 pt-4 dark:border-neutral-low-300 md:flex-nowrap',
            className
          )}
        >
          <div className="grid w-full grid-flow-col grid-cols-[6.25rem_1fr] gap-x-4 gap-y-2 md:grid-cols-[6.5rem_1fr] md:gap-x-6 md:gap-y-1">
            <div className="col-span-1 inline-flex flex-col self-start rounded-2 md:row-span-2 md:size-[120px] md:flex-row md:p-2">
              <Image
                alt={toOfferImageAltText(offerTitle)}
                className={twJoin(
                  'size-full rounded-4 border border-neutral-high-200 dark:border-neutral-low-300',
                  offerStatusName === OFFER.STATUSES.FINISHED && 'opacity-30'
                )}
                grayscale={offerStatusName === OFFER.STATUSES.FINISHED}
                height={100}
                loading={offerImagePriority ? null : 'lazy'}
                priority={offerImagePriority}
                src={getOfferImageLink(offerPhoto, 100)}
                srcSet={`
                  ${getOfferImageLink(offerPhoto, 120)} 120w,
                  ${getOfferImageLink(offerPhoto, 180)} 180w,
                  ${getOfferImageLink(offerPhoto, 300)} 300w,
                `}
                weight={100}
              />
              {!showOfferStatusBadge && (
                <Badge
                  className="absolute left-4 top-4"
                  offerUserVisibility={offerUserVisibility}
                />
              )}
              {showGoToStoreButton &&
                (isValidApprovedOffer(offerStatusName) ||
                  offerStatusName === OFFER.STATUSES.RECENTS) && (
                  <div className="mt-4 block md:hidden">
                    <GoToStoreButton
                      offerCoupon={offerCoupon}
                      offerId={offerId}
                      offerStatusName={offerStatusName}
                      offerUserVisibility={offerUserVisibility}
                      size="size3"
                      type="primary"
                    />
                  </div>
                )}
            </div>
            <div className="flex flex-col justify-between lg:row-span-2">
              <div className="flex flex-col-reverse items-start justify-between lg:flex-row">
                <Text
                  className={twJoin(
                    'break-words',
                    showOfferStatusBadge && 'xl:max-w-4/5',
                    showOfferPrice ? 'md:line-clamp-3' : 'md:line-clamp-1'
                  )}
                >
                  {offerTitle}
                </Text>
                {showOfferStatusBadge && (
                  <OfferStatusBadge
                    className="mb-2 xl:mb-0"
                    offerStatusName={offerStatusName}
                  />
                )}
              </div>
              {offerTags.length > 0 && (
                <Tags
                  className="mt-2"
                  offerTags={offerTags}
                  offerUserVisibility={offerUserVisibility}
                  limitOffersTagsByOfferCardSize
                />
              )}
              <div
                className={twJoin(
                  'flex items-end',
                  showOfferPrice
                    ? 'justify-between'
                    : 'justify-start lg:justify-end'
                )}
              >
                {showOfferPrice && (
                  <Price
                    offerOldPrice={offerOldPrice}
                    offerPrice={offerPrice}
                    offerPriceType={offerPriceType}
                    offerStatusName={offerStatusName}
                  />
                )}
                <div className="flex flex-col gap-0 md:gap-4">
                  {showGoToStoreButton &&
                    (isValidApprovedOffer(offerStatusName) ||
                      offerStatusName === OFFER.STATUSES.RECENTS) && (
                      <div className="hidden md:block">
                        <GoToStoreButton
                          offerCoupon={offerCoupon}
                          offerId={offerId}
                          offerSlug={offerSlug}
                          offerStatusName={offerStatusName}
                          offerUserVisibility={offerUserVisibility}
                          size="size3"
                          type="primary"
                        />
                      </div>
                    )}
                  <OfferStats
                    className="mt-3 md:mt-0"
                    offerClicks={offerClicks}
                    offerComments={offerComments}
                    offerLikes={offerLikes}
                    selectedOfferStats={selectedOfferStats}
                  />
                </div>
              </div>
            </div>
          </div>
          {showPublishDetails && (
            <PublishDetails
              publishedOn={offerPublished}
              storeDomain={storeDomain}
              storeImage={storeImage}
              userName={userName}
            />
          )}
        </div>
      </Anchor>
    </NextLink>
  );
};

OfferSearchListCard.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  disabled: PropTypes.bool,
  gaEventCategory: PropTypes.string.isRequired,
  offerClicks: PropTypes.number,
  offerComments: PropTypes.number.isRequired,
  offerImagePriority: PropTypes.bool,
  offerLikes: PropTypes.number.isRequired,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number,
  offerPriceType: PropTypes.string,
  offerPublished: PropTypes.string,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string.isRequired,
    })
  ),
  offerTitle: PropTypes.string.isRequired,
  selectedOfferStats: PropTypes.shape().isRequired,
  showOfferPrice: PropTypes.bool,
  showOfferStatusBadge: PropTypes.bool,
  showPublishDetails: PropTypes.bool,
  showGoToStoreButton: PropTypes.bool,
  storeDomain: PropTypes.string,
  storeImage: PropTypes.string,
  userName: PropTypes.string,
};

export default OfferSearchListCard;
